import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTenantLocation } from '../../context/LocationContext';
import { api } from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  CheckCircle2, AlertTriangle, XCircle, Circle, MapPin, ArrowRight, Loader2,
} from 'lucide-react';

/**
 * Compact horizontal strip for the Dashboard: one chip per location showing
 * today's sync-status at a glance. Multi-venue owners can spot problems
 * without leaving home. Hidden entirely when the tenant has fewer than 2
 * locations (no point showing a "strip" for a single venue).
 */
export function LocationHealthStrip() {
  const { token } = useAuth();
  const { locations: contextLocations, setCurrentLocationId } = useTenantLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const r = await api.compareLocations(token, { range: 'today' });
      setRows(r.data.locations || []);
    } catch {
      setRows([]);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load, contextLocations.length]);

  if (contextLocations.length < 2) return null; // single-venue: skip
  if (loading) {
    return (
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-3 flex items-center gap-2 text-slate-500 text-xs">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading location health…
        </CardContent>
      </Card>
    );
  }
  if (rows.length === 0) return null;

  const failing = rows.filter((r) => r.sync_status === 'failing').length;
  const degraded = rows.filter((r) => r.sync_status === 'degraded').length;
  const healthy = rows.filter((r) => r.sync_status === 'healthy').length;
  const idle = rows.filter((r) => r.sync_status === 'idle').length;

  const jumpTo = (loc) => {
    setCurrentLocationId(loc.location_id);
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800" data-testid="location-health-strip">
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
            <MapPin className="w-3.5 h-3.5" />
            Location health today
            <span className="text-slate-600">·</span>
            <span className="text-slate-400 normal-case tracking-normal">
              {healthy > 0 && <span className="text-emerald-300">{healthy} healthy</span>}
              {healthy > 0 && (failing + degraded + idle) > 0 && <span className="text-slate-600"> · </span>}
              {degraded > 0 && <span className="text-amber-300">{degraded} degraded</span>}
              {degraded > 0 && (failing + idle) > 0 && <span className="text-slate-600"> · </span>}
              {failing > 0 && <span className="text-rose-300">{failing} failing</span>}
              {failing > 0 && idle > 0 && <span className="text-slate-600"> · </span>}
              {idle > 0 && <span className="text-slate-500">{idle} idle</span>}
            </span>
          </div>
          <button
            type="button"
            onClick={() => navigate('/compare')}
            className="text-xs text-blue-300 hover:text-white flex items-center gap-1"
            data-testid="health-strip-compare-link"
          >
            Compare in detail <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {rows.map((r) => (
            <LocationChip key={r.location_id} loc={r} onClick={() => jumpTo(r)} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function LocationChip({ loc, onClick }) {
  const cfg = {
    healthy:  { Icon: CheckCircle2,  cls: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/20' },
    degraded: { Icon: AlertTriangle, cls: 'bg-amber-500/10 text-amber-300 border-amber-500/30 hover:bg-amber-500/20' },
    failing:  { Icon: XCircle,       cls: 'bg-rose-500/10 text-rose-300 border-rose-500/30 hover:bg-rose-500/20' },
    idle:     { Icon: Circle,        cls: 'bg-slate-800/60 text-slate-400 border-slate-700 hover:bg-slate-800' },
  }[loc.sync_status] || { Icon: Circle, cls: 'bg-slate-800 text-slate-400 border-slate-700' };
  const { Icon, cls } = cfg;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 rounded-md border ${cls} px-2.5 py-1.5 transition-colors`}
      data-testid={`health-chip-${loc.location_id}`}
      title={`${loc.location_name}: ${loc.sync_status}${loc.sync_health_pct !== null ? ` · ${loc.sync_health_pct}%` : ''}`}
    >
      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
      <div className="text-left leading-tight">
        <div className="text-xs font-medium truncate max-w-[140px]">{loc.location_name}</div>
        <div className="text-[9px] opacity-80 font-mono">
          {loc.sync_health_pct === null ? 'no syncs today' : `${loc.sync_health_pct}%`}
          {loc.sync_failed_window > 0 && ` · ${loc.sync_failed_window} failed`}
        </div>
      </div>
      {loc.is_default && (
        <Badge className="bg-black/20 text-white/60 border-white/10 text-[8px] px-1 py-0">DEF</Badge>
      )}
    </button>
  );
}
